import { useEffect, useRef, useState } from "react";

import { useSelector } from "react-redux";

import { useUpdateFieldStatuses } from "@/hooks/mutations/use-assessments";
import { RootState } from "@/store";

import {
  AssessmentColumnNameEnum,
  AssessmentFieldStatusSchema,
  AssessmentResultStatusEnum,
  AssessmentWithFieldStatusesSchema,
} from "@api/generated/types.gen";

import { ASSESSMENT_SECTIONS } from "../constants/sections";

// AssessmentWithFieldStatusesSchema types most section fields as
// AssessmentFieldStatusSchema | null, but not all AssessmentColumnNameEnum
// keys resolve to that shape on the schema (e.g. "notes" is a plain
// string | null there) — so indexing by section.key returns a wider type
// than a single field. Narrow to the { status } shape this hook actually
// reads instead of asserting the field is always AssessmentFieldStatusSchema.
const isAssessmentFieldStatus = (
  value: unknown
): value is AssessmentFieldStatusSchema =>
  !!value && typeof value === "object" && "status" in value;

type UseAutoExportProps = {
  assessmentData?: AssessmentWithFieldStatusesSchema;
  assessmentId?: string;
  caseNumber?: string;
  checkInterval?: number; // in milliseconds, default 3000 (3 seconds)
  enabled?: boolean; // Whether auto-export is enabled
};

export const useAutoExport = ({
  assessmentData,
  assessmentId,
  caseNumber,
  checkInterval = 3000,
  enabled = true,
}: UseAutoExportProps) => {
  const [lastExportTime, setLastExportTime] = useState<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const assessmentDataRef = useRef(assessmentData);
  const updateFieldStatuses = useUpdateFieldStatuses();
  const isExporting = updateFieldStatuses.isPending;
  // The interval callback below is created once (in startAutoExport, not a
  // useEffect) and keeps running against that stale closure, so it needs a
  // ref to read the CURRENT isExporting rather than the value from whenever
  // the interval started — mirrors assessmentDataRef's same reasoning.
  const isExportingRef = useRef(isExporting);

  // Keep refs in sync with latest values the interval's frozen closure reads.
  useEffect(() => {
    assessmentDataRef.current = assessmentData;
  }, [assessmentData]);
  useEffect(() => {
    isExportingRef.current = isExporting;
  }, [isExporting]);

  // Check if there are active transcription tasks for this case
  const activeTranscriptionTasks = useSelector(
    (state: RootState) => state.transcription?.activeTasks || {}
  );

  const hasActiveTranscriptionTask = () => {
    if (!caseNumber || Object.keys(activeTranscriptionTasks).length === 0) {
      return false;
    }
    // Check if any active task matches the current case number
    return Object.values(activeTranscriptionTasks).some(
      (task): boolean =>
        !!task &&
        typeof task === "object" &&
        "caseNumber" in task &&
        (task as { caseNumber?: string }).caseNumber === caseNumber
    );
  };

  // Get fields that need to be exported (not EXPORTED status)
  const getUnprocessedFields = () => {
    const currentAssessmentData = assessmentDataRef.current;
    if (!currentAssessmentData) return [];

    const unprocessedFields: {
      column_name: AssessmentColumnNameEnum;
      status: AssessmentResultStatusEnum;
    }[] = [];
    const seenKeys = new Set<string>();

    // Not every AssessmentColumnNameEnum member resolves to an
    // AssessmentFieldStatusSchema field on the schema (e.g. "notes" is a
    // plain string there), so index via a loosely-typed view and narrow
    // with isAssessmentFieldStatus below instead of asserting the shape.
    const dataByKey = currentAssessmentData as unknown as Record<string, unknown>;

    ASSESSMENT_SECTIONS.forEach((section) => {
      if (section.key && !seenKeys.has(section.key) && dataByKey[section.key]) {
        seenKeys.add(section.key);
        const field = dataByKey[section.key];
        // Check if the field has status information and is not EXPORTED
        if (isAssessmentFieldStatus(field)) {
          if (field.status !== "EXPORTED") {
            unprocessedFields.push({
              column_name: section.key,
              status: "EXPORTED",
            });
          }
        }
      }
    });

    return unprocessedFields;
  };

  // Check if there are unprocessed fields
  const hasUnprocessedFields = () => {
    return getUnprocessedFields().length > 0;
  };

  // Auto-export unprocessed fields
  const autoExportFields = async () => {
    // updateFieldStatuses.isPending already tracks whether a call is in
    // flight — read via the ref so this guard is live even when called from
    // the interval's frozen closure (see isExportingRef above).
    if (!assessmentId || !caseNumber || isExportingRef.current) {
      return;
    }

    // Don't auto-export if there's an active transcription task for this case
    if (hasActiveTranscriptionTask()) {
      return;
    }

    const unprocessedFields = getUnprocessedFields();
    if (unprocessedFields.length === 0) {
      return;
    }

    // Throttle exports - don't export more than once every 5 seconds
    const now = Date.now();
    if (now - lastExportTime < 5000) {
      return;
    }

    try {
      setLastExportTime(now);
      // Set synchronously, before the await — updateFieldStatuses.isPending
      // (which the effect above syncs into this same ref) only flips true
      // after the mutation function has started running, leaving a brief
      // window where a second call (e.g. triggerAutoExport firing right
      // after a save, racing the next interval tick) wouldn't yet see this
      // export as in flight.
      isExportingRef.current = true;

      await updateFieldStatuses.mutateAsync({
        assessmentId,
        caseNumber,
        fieldStatuses: unprocessedFields,
      });

      // Optimistically update the ref to mark these fields as EXPORTED
      // This prevents the interval from finding the same fields again before the refetch completes
      if (assessmentDataRef.current) {
        const updatedData = { ...assessmentDataRef.current };
        const updatedDataByKey = updatedData as unknown as Record<string, unknown>;
        unprocessedFields.forEach((field) => {
          const fieldKey = field.column_name;
          const existingField = updatedDataByKey[fieldKey];
          if (isAssessmentFieldStatus(existingField)) {
            updatedDataByKey[fieldKey] = {
              ...existingField,
              status: "EXPORTED",
            };
          }
        });
        assessmentDataRef.current = updatedData;
      }

      console.log(`Auto-exported ${unprocessedFields.length} assessment fields`);
    } catch (error) {
      console.error("Error auto-exporting assessment fields:", error);
    }
  };

  // Start auto-export monitoring
  const startAutoExport = () => {
    // Don't start if there's already an active transcription task
    if (hasActiveTranscriptionTask()) {
      stopAutoExport();
      return;
    }

    // Don't restart if interval is already running
    if (intervalRef.current) {
      return;
    }

    intervalRef.current = setInterval(() => {
      // Stop the interval if a transcription task becomes active
      if (hasActiveTranscriptionTask()) {
        stopAutoExport();
        return;
      }

      // Don't call autoExportFields if it's already running
      if (isExportingRef.current) {
        return;
      }

      if (enabled && hasUnprocessedFields() && !hasActiveTranscriptionTask()) {
        autoExportFields();
      }
    }, checkInterval);
  };

  // Stop auto-export monitoring
  const stopAutoExport = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // Manual trigger for immediate export
  const triggerAutoExport = () => {
    if (enabled && !hasActiveTranscriptionTask()) {
      autoExportFields();
    }
  };

  // Start monitoring when component mounts or dependencies change
  useEffect(() => {
    // Don't start auto-export if there's an active transcription task
    if (hasActiveTranscriptionTask()) {
      stopAutoExport();
      return;
    }

    // Only start if we have the required data and interval is not already running
    if (enabled && assessmentId && caseNumber && assessmentData) {
      // Only start if interval is not already running
      if (!intervalRef.current) {
        startAutoExport();
      }
    } else {
      stopAutoExport();
    }

    return () => {
      stopAutoExport();
    };
  }, [
    enabled,
    assessmentId,
    caseNumber,
    // Note: assessmentData is intentionally not in dependencies to avoid restarting
    // the interval on every refetch. The interval checks for unprocessed fields on each tick.
    checkInterval,
    activeTranscriptionTasks,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAutoExport();
    };
  }, []);

  return {
    hasUnprocessedFields: hasUnprocessedFields(),
    isExporting,
    triggerAutoExport,
    startAutoExport,
    stopAutoExport,
    unprocessedFieldsCount: getUnprocessedFields().length,
  };
};
